import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Button, Switch, Tooltip, Input, Badge } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  PlayCircleOutlined,
  SearchOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { ClientRules, CommonRules } from "../styles/rules/index";
import { acceptSpan, markSpanRejected } from "../styles/Customstyles";
import { diff_match_patch } from "diff-match-patch";

export const ClientCopyeditPanel = ({ editor }) => {
  const [activeTab, setActiveTab] = useState("suggestions");
  const [ruleScope, setRuleScope] = useState("all");
  const [enabledRules, setEnabledRules] = useState({});
  const [ruleSearch, setRuleSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [filterStatus, setFilterStatus] = useState("to_review"); // 'to_review', 'accepted', 'dismissed'
  const [acceptedSuggestions, setAcceptedSuggestions] = useState([]);
  const [dismissedSuggestions, setDismissedSuggestions] = useState([]);

  const editorRef = useRef(editor);
  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  // Combine rules list based on the active scope filter
  const allAvailableRules = useMemo(() => {
    const rules = [];
    if (ruleScope === "all" || ruleScope === "common") {
      rules.push(...CommonRules.map(r => ({ ...r, origin: "General" })));
    }
    if (ruleScope === "all" || ruleScope === "client") {
      const client = ClientRules["tsp"];
      if (client) {
        rules.push(...client.rules.map(r => ({ ...r, origin: client.name })));
      }
    }
    return rules;
  }, [ruleScope]);

  // Initialize enabled rules map when available rules change
  useEffect(() => {
    const initial = {};
    allAvailableRules.forEach(rule => {
      initial[rule.id] = true; // default enabled
    });
    setEnabledRules(initial);
  }, [allAvailableRules]);

  // Run the copyediting scan
  const runScan = useCallback(() => {
    const ed = editorRef.current;
    if (!ed) return;

    setScanning(true);
    try {
      const doc = ed.getDoc();
      if (doc) {
        doc.querySelectorAll(".client-style-highlight").forEach(span => {
          const p = span.parentNode;
          if (p) {
            while (span.firstChild) p.insertBefore(span.firstChild, span);
            p.removeChild(span);
          }
        });
      }

      const activeRules = allAvailableRules.filter(r => enabledRules[r.id]);
      const newSuggestions = [];

      activeRules.forEach(rule => {
        const matches = rule.execute(ed);
        if (matches && matches.length > 0) {
          newSuggestions.push({
            ruleId: rule.id,
            ruleName: rule.name,
            description: rule.description,
            doc: rule.doc,
            origin: rule.origin,
            items: matches.map(m => ({ ...m, status: "pending" }))
          });
        }
      });
      setSuggestions(newSuggestions);
      setAcceptedSuggestions([]);
      setDismissedSuggestions([]);
      setFilterStatus("to_review");
      setActiveTab("suggestions");
    } catch (err) {
      console.error("Copyediting scan failed:", err);
    } finally {
      setScanning(false);
    }
  }, [allAvailableRules, enabledRules]);

  const handleApprove = (ruleId, itemIndex) => {
    const ruleGroup = suggestions.find(s => s.ruleId === ruleId);
    if (!ruleGroup) return;
    const item = ruleGroup.items[itemIndex];
    if (!item) return;

    const ed = editorRef.current;
    if (!ed) return;

    ed.undoManager.transact(() => {
      acceptSpan(ed, item.uid, item.suggested);
    });

    setAcceptedSuggestions(prev => [...prev, { ...item, ruleName: ruleGroup.ruleName }]);
    setSuggestions(prev =>
      prev.map(sg => {
        if (sg.ruleId !== ruleId) return sg;
        return {
          ...sg,
          items: sg.items.filter((_, idx) => idx !== itemIndex)
        };
      }).filter(sg => sg.items.length > 0)
    );
  };

  const handleReject = (ruleId, itemIndex) => {
    const ruleGroup = suggestions.find(s => s.ruleId === ruleId);
    if (!ruleGroup) return;
    const item = ruleGroup.items[itemIndex];
    if (!item) return;

    const ed = editorRef.current;
    if (!ed) return;

    markSpanRejected(ed, item.uid);

    setDismissedSuggestions(prev => [...prev, { ...item, ruleName: ruleGroup.ruleName }]);
    setSuggestions(prev =>
      prev.map(sg => {
        if (sg.ruleId !== ruleId) return sg;
        return {
          ...sg,
          items: sg.items.filter((_, idx) => idx !== itemIndex)
        };
      }).filter(sg => sg.items.length > 0)
    );
  };

  const handleApproveAll = () => {
    const ed = editorRef.current;
    if (!ed) return;

    ed.undoManager.transact(() => {
      suggestions.forEach(group => {
        group.items.forEach(item => {
          acceptSpan(ed, item.uid, item.suggested);
          setAcceptedSuggestions(prev => [...prev, { ...item, ruleName: group.ruleName }]);
        });
      });
    });

    setSuggestions([]);
  };

  const handleRejectAll = () => {
    const ed = editorRef.current;
    if (!ed) return;

    suggestions.forEach(group => {
      group.items.forEach(item => {
        markSpanRejected(ed, item.uid);
        setDismissedSuggestions(prev => [...prev, { ...item, ruleName: group.ruleName }]);
      });
    });

    setSuggestions([]);
  };

  const scrollToItem = (item) => {
    const ed = editorRef.current;
    const doc = ed?.getDoc();
    if (!doc || !ed) return;

    const span = doc.querySelector(`span[data-suggestion-uid="${item.uid}"]`);
    if (!span) return;

    ed.selection.scrollIntoView(span);
    ed.selection.select(span);
    ed.focus();
  };

  const filteredRulesList = useMemo(() => {
    return allAvailableRules.filter(rule => {
      const term = ruleSearch.toLowerCase();
      return (
        rule.name.toLowerCase().includes(term) ||
        rule.description.toLowerCase().includes(term)
      );
    });
  }, [allAvailableRules, ruleSearch]);

  const toggleRule = (ruleId, checked) => {
    setEnabledRules(prev => ({
      ...prev,
      [ruleId]: checked
    }));
  };

  const toReviewCount = suggestions.reduce((sum, sg) => sum + sg.items.length, 0);
  const acceptedCount = acceptedSuggestions.length;
  const dismissedCount = dismissedSuggestions.length;
  const totalEdits = toReviewCount + acceptedCount + dismissedCount;
  const progressPercent = totalEdits > 0 ? Math.round(((acceptedCount + dismissedCount) / totalEdits) * 100) : 0;

  return (
    <div className="flex flex-col h-full bg-[#FFFFFF]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {/* Title Header */}
      <div className="flex items-center justify-between border-b border-[#E7E4DD] pb-3 mb-3">
        <span className="text-[17px] font-semibold text-[#17171A]">Copyedit Review</span>
        <button
          onClick={runScan}
          disabled={scanning}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-[#4F46E5] hover:bg-[#4338CA] transition-colors disabled:opacity-50"
        >
          <PlayCircleOutlined />
          <span>{scanning ? "Scanning..." : "Scan"}</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex bg-[#F4F3EF] p-1 rounded-lg mb-4">
        <button
          onClick={() => setActiveTab("suggestions")}
          className={`flex-1 py-1.5 text-xs font-semibold text-center rounded-md transition-all ${
            activeTab === "suggestions"
              ? "bg-[#FFFFFF] text-[#17171A] shadow-sm"
              : "text-[#6B6B76] hover:text-[#17171A]"
          }`}
        >
          Proposed Edits
        </button>
        <button
          onClick={() => setActiveTab("rules")}
          className={`flex-1 py-1.5 text-xs font-semibold text-center rounded-md transition-all ${
            activeTab === "rules"
              ? "bg-[#FFFFFF] text-[#17171A] shadow-sm"
              : "text-[#6B6B76] hover:text-[#17171A]"
          }`}
        >
          Style Rules
        </button>
      </div>

      {activeTab === "suggestions" ? (
        <div className="flex flex-col gap-4 flex-1 overflow-y-auto">
          {/* Progress Section */}
          <div className="bg-[#FAF9F6] border border-[#E7E4DD] rounded-xl p-3.5 flex flex-col gap-3">
            <div className="flex items-center justify-between text-xs font-semibold">
              <span className="text-[#6B6B76]">Review Progress</span>
              <span className="text-[#17171A]">{progressPercent}%</span>
            </div>
            <div className="w-full bg-[#E7E4DD] h-1.5 rounded-full overflow-hidden">
              <div
                className="bg-[#4F46E5] h-full rounded-full transition-all duration-350"
                style={{ width: `${progressPercent}%` }}
              ></div>
            </div>

            {/* Quick Actions (Approve/Reject All) */}
            {toReviewCount > 0 && (
              <div className="flex gap-2 border-t border-[#E7E4DD] pt-3 mt-1">
                <button
                  onClick={handleApproveAll}
                  className="flex-1 py-1.5 rounded-lg border border-[#D5F5E3] bg-[#E8F8F5] text-[#196F3D] text-[11px] font-semibold hover:bg-[#D5F5E3] transition-colors"
                >
                  ✓ Accept All
                </button>
                <button
                  onClick={handleRejectAll}
                  className="flex-1 py-1.5 rounded-lg border border-[#FADBD8] bg-[#FDEDEC] text-[#943126] text-[11px] font-semibold hover:bg-[#FADBD8] transition-colors"
                >
                  ✕ Dismiss All
                </button>
              </div>
            )}
          </div>

          {/* Status Sub-tabs */}
          <div className="flex gap-1.5 border-b border-[#E7E4DD] pb-2 text-xs">
            <button
              onClick={() => setFilterStatus("to_review")}
              className={`pb-1.5 font-semibold px-1 border-b-2 transition-all ${
                filterStatus === "to_review"
                  ? "border-[#4F46E5] text-[#4F46E5]"
                  : "border-transparent text-[#6B6B76] hover:text-[#17171A]"
              }`}
            >
              To Review ({toReviewCount})
            </button>
            <button
              onClick={() => setFilterStatus("accepted")}
              className={`pb-1.5 font-semibold px-1 border-b-2 transition-all ${
                filterStatus === "accepted"
                  ? "border-[#4F46E5] text-[#4F46E5]"
                  : "border-transparent text-[#6B6B76] hover:text-[#17171A]"
              }`}
            >
              Accepted ({acceptedCount})
            </button>
            <button
              onClick={() => setFilterStatus("dismissed")}
              className={`pb-1.5 font-semibold px-1 border-b-2 transition-all ${
                filterStatus === "dismissed"
                  ? "border-[#4F46E5] text-[#4F46E5]"
                  : "border-transparent text-[#6B6B76] hover:text-[#17171A]"
              }`}
            >
              Dismissed ({dismissedCount})
            </button>
          </div>

          {/* Scope Filters */}
          {filterStatus === "to_review" && (
            <div className="flex gap-2 text-xs font-semibold">
              <button
                onClick={() => setRuleScope("all")}
                className={`px-3 py-1 rounded-full border transition-all ${
                  ruleScope === "all"
                    ? "bg-[#EEF0FF] border-[#4F46E5] text-[#4F46E5]"
                    : "bg-[#FFFFFF] border-[#E7E4DD] text-[#6B6B76] hover:text-[#17171A]"
                }`}
              >
                All
              </button>
              <button
                onClick={() => setRuleScope("client")}
                className={`px-3 py-1 rounded-full border transition-all ${
                  ruleScope === "client"
                    ? "bg-[#EEF0FF] border-[#4F46E5] text-[#4F46E5]"
                    : "bg-[#FFFFFF] border-[#E7E4DD] text-[#6B6B76] hover:text-[#17171A]"
                }`}
              >
                Client (TSP)
              </button>
              <button
                onClick={() => setRuleScope("common")}
                className={`px-3 py-1 rounded-full border transition-all ${
                  ruleScope === "common"
                    ? "bg-[#EEF0FF] border-[#4F46E5] text-[#4F46E5]"
                    : "bg-[#FFFFFF] border-[#E7E4DD] text-[#6B6B76] hover:text-[#17171A]"
                }`}
              >
                Common
              </button>
            </div>
          )}

          {/* Suggestions List */}
          <div className="flex flex-col gap-3.5 flex-1">
            {filterStatus === "to_review" && suggestions.length > 0 ? (
              suggestions.map(group => (
                <div key={group.ruleId} className="flex flex-col gap-2">
                  {group.items.map((item, idx) => (
                    <div
                      key={item.uid}
                      onClick={() => scrollToItem(item)}
                      className="bg-white border border-[#E7E4DD] hover:border-[#4F46E5] transition-all rounded-xl p-3.5 shadow-sm cursor-pointer relative"
                    >
                      {/* Rule metadata */}
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[12px] font-bold text-[#17171A]">{group.ruleName}</span>
                        <span className="text-[9px] font-semibold text-[#4F46E5] bg-[#EEF0FF] border border-[#C7CBD3] px-1.5 py-0.5 rounded">
                          {group.origin}
                        </span>
                      </div>

                      {/* Side-by-side or stack diff block */}
                      <div className="flex flex-col gap-1.5 text-xs mb-3 font-mono">
                        <div className="flex items-start gap-1 bg-[#FDEDEC] text-[#943126] border border-[#FADBD8] rounded px-2.5 py-1.5">
                          <span className="font-bold opacity-60">-</span>
                          <span className="break-all">{item.text || "(empty)"}</span>
                        </div>
                        <div className="flex items-start gap-1 bg-[#E8F8F5] text-[#196F3D] border border-[#D5F5E3] rounded px-2.5 py-1.5">
                          <span className="font-bold opacity-60">+</span>
                          <span className="break-all">{item.suggested || "(removed)"}</span>
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex justify-end gap-2" onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => handleReject(group.ruleId, idx)}
                          className="flex items-center justify-center w-7 h-7 rounded-lg border border-[#E7E4DD] text-[#EF4444] hover:bg-[#FDEDEC] transition-colors"
                        >
                          ✕
                        </button>
                        <button
                          onClick={() => handleApprove(group.ruleId, idx)}
                          className="flex items-center justify-center px-3 h-7 rounded-lg bg-[#4F46E5] hover:bg-[#4338CA] text-white font-semibold text-[11px] transition-colors"
                        >
                          Approve
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ))
            ) : filterStatus === "to_review" ? (
              <div className="text-center text-xs text-[#9A988F] py-8">
                No active suggestions. Run "Scan" to find potential edits.
              </div>
            ) : null}

            {filterStatus === "accepted" && (
              acceptedSuggestions.length > 0 ? (
                acceptedSuggestions.map((item, idx) => (
                  <div key={idx} className="bg-white border border-[#E7E4DD] rounded-xl p-3.5 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[12px] font-bold text-[#17171A]">{item.ruleName}</span>
                      <span className="text-[9px] font-bold text-[#196F3D] bg-[#E8F8F5] px-1.5 py-0.5 rounded">
                        ✓ Accepted
                      </span>
                    </div>
                    <div className="text-xs font-mono bg-[#E8F8F5] text-[#196F3D] border border-[#D5F5E3] rounded px-2.5 py-1.5 break-all">
                      {item.suggested}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-xs text-[#9A988F] py-8">No accepted suggestions.</div>
              )
            )}

            {filterStatus === "dismissed" && (
              dismissedSuggestions.length > 0 ? (
                dismissedSuggestions.map((item, idx) => (
                  <div key={idx} className="bg-white border border-[#E7E4DD] rounded-xl p-3.5 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[12px] font-bold text-[#17171A]">{item.ruleName}</span>
                      <span className="text-[9px] font-bold text-[#943126] bg-[#FDEDEC] px-1.5 py-0.5 rounded">
                        ✕ Dismissed
                      </span>
                    </div>
                    <div className="text-xs font-mono bg-[#FDEDEC] text-[#943126] border border-[#FADBD8] rounded px-2.5 py-1.5 break-all">
                      {item.text}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-xs text-[#9A988F] py-8">No dismissed suggestions.</div>
              )
            )}
          </div>
        </div>
      ) : (
        /* Rules Config tab */
        <div className="flex flex-col gap-3.5 flex-1 overflow-y-auto">
          <div className="bg-[#FAF9F6] border border-[#E7E4DD] rounded-xl p-3 flex flex-col gap-3">
            <Input
              placeholder="Search copyedit rules..."
              prefix={<SearchOutlined className="text-[#9A988F]" />}
              value={ruleSearch}
              onChange={e => setRuleSearch(e.target.value)}
              className="rounded-lg"
              allowClear
            />
          </div>

          <div className="flex flex-col gap-2.5">
            {filteredRulesList.map(rule => (
              <div
                key={rule.id}
                className="flex items-start justify-between p-3 bg-white border border-[#E7E4DD] rounded-xl shadow-sm hover:shadow transition-shadow"
              >
                <div className="flex flex-col gap-1 pr-4 flex-1">
                  <span className="text-xs font-bold text-[#17171A]">{rule.name}</span>
                  <span className="text-[11px] text-[#6B6B76]">{rule.description}</span>
                </div>
                <Switch
                  checked={enabledRules[rule.id] !== false}
                  onChange={(checked) => toggleRule(rule.id, checked)}
                  size="small"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
export default ClientCopyeditPanel;
